import {
	assetConfigCreator,
	MapModes,
	ReactMapProps,
	SourceType,
} from '@innovyze/inno-map';
import { GeoJsonInputType } from '@innovyze/inno-map/map/services/types';
import {
	Button,
	InfoCard,
	PageWrapper,
	STATUS_ENUM,
} from '@innovyze/stylovyze';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import {
	peekSensorReset,
	pollSensorTroubleshootingStop,
	resetCreateConfigurationStatus,
	restoreStatusStore,
} from '@Actions';
import { getSystemConnections } from '@Actions/systemConnections';
import { SensorFormMode } from '@Components/SensorConfigurationForm/SensorConfigurationForm';
import SensorConfigurationMap from '@Components/SensorConfigurationMap/SensorConfigurationMap';
import {
	useSensorFormState,
	useSensorTroubleshoot,
	useSensorTroubleshootMessages,
} from '@Hooks';
import { getValue } from '@Selectors';
import { useGlobalization } from '@Translations';
import { MAIN_ROUTES } from '@Types/routes';
import {
	BodyGridStyled,
	ContainerStyled,
	MapCardStyle,
} from './NewSensor.styles';
import FormCard from '@Pages/EditSensor/FormCard';
import PeekDialog from '@Pages/EditSensor/PeekDialog';
import { SpinnerStatusIndicator } from '@Pages/EditSensor/SpinnerStatusIndicator';
import { Tooltip } from '@mui/material';
import SensorTroubleShootDialog from '@Pages/EditSensor/SensorTroubleShootDialog';

const NewSensor = () => {
	const { t } = useGlobalization();
	const history = useHistory();
	const dispatch = useDispatch();
	const [
		formData,
		formErrors,
		updateFormData,
		createUpdateConfigurationFn,
		,
		,
		peekSensorConnection,
	] = useSensorFormState();

	const [showTbltModal, setShowTbltModal] = useState(false);

	const { sensorTroubleshooting, isCurrentlyTesting } =
		useSensorTroubleshoot();

	const shouldRenderTest = useMemo(
		() =>
			formData.dataSourceType === 'ODBC' ||
			formData.dataSourceType === 'OLEDB' ||
			formData.dataSourceType === 'FORM',
		[formData.dataSourceType],
	);

	const shouldDisableTest = useMemo(
		() => formData.dataSourceType === 'FORM',
		[formData.dataSourceType],
	);

	const [showPeekModal, setShowPeekModal] = React.useState(false);

	const [position, setPosition] = React.useState({
		center: [0, 0],
		zoom: 10,
	});
	const [dataSources, setDataSources] = React.useState([
		{
			type: SourceType.GEOJSON,
			id: 'sensor',
			data: {} as GeoJsonInputType,
		},
	] as ReactMapProps['dataServices']);

	const {
		systemConnections: { list: rows },
		configurations: { createSensorStatus },
		sensorTypes: { types: sensorTypes, units },
		editSensorData: { peek: peekState },
		systemConnections: { initialized: isSystemConnectionsInitialized },
	} = useSelector(getValue);

	const sensorTbltingMsgs = useSensorTroubleshootMessages(
		sensorTroubleshooting,
		formData.dataSourceId,
		rows,
	);

	useEffect(() => {
		if (
			sensorTroubleshooting?.state === 'FINISHED' &&
			!isCurrentlyTesting &&
			Object.keys(sensorTbltingMsgs.messages).length
		) {
			setShowTbltModal(true);
		} else {
			setShowTbltModal(false);
		}
	}, [sensorTbltingMsgs]);

	const layerConfig = useMemo(() => {
		const sensorDataSource = dataSources?.[0];
		if (sensorDataSource && Object.keys(sensorDataSource.data).length) {
			return [assetConfigCreator(sensorDataSource)];
		}
	}, [dataSources]);

	// this function is to check if the lat and lng are valid before updating
	// the map. If the map is updated with bad coords it crashes
	// Latitude range in degrees is -90 and +90
	// Longitude is in the range -180 and +180
	function validateLatLng(_lat: number, _lng: number): boolean {
		let valid = false;
		if (_lat >= -90 && _lat <= 90 && _lng >= -180 && _lng <= 180) {
			valid = true;
		}
		return valid;
	}

	function updateMap(lat: number, lng: number) {
		setPosition({
			center: [lat, lng],
			zoom: 15,
		});
		setDataSources([
			{
				type: SourceType.GEOJSON,
				id: 'sensor',
				displayName: 'Location',
				data: {
					type: 'FeatureCollection',
					features: [
						{
							type: 'Feature',
							geometry: {
								type: 'Point',
								coordinates: [lat, lng],
							},
							properties: {
								_id: 'sensor',
								assetType: 'sensor',
							},
						},
					],
				} as GeoJsonInputType,
			},
		] as ReactMapProps['dataServices']);
	}

	function addNewSensorConfig() {
		createUpdateConfigurationFn(true);
	}

	function cancelConfig() {
		dispatch(pollSensorTroubleshootingStop());
		history.push(MAIN_ROUTES.SensorConfiguration);
	}

	function mapChange(data: string) {
		try {
			const coor = data.split(',');
			const [lat, lng] = [parseFloat(coor[0]), parseFloat(coor[1])];
			if (coor.length == 2 && !isNaN(lat) && !isNaN(lng)) {
				try {
					// need to validate coordinates before updating the map or it crashes
					if (validateLatLng(lat, lng)) {
						updateMap(lng, lat);
						console.log(dataSources);
					}
				} finally {
					console.log('Updating map end', coor);
				}
			}
		} catch (error) {
			console.log('invalid coordinates');
		}
	}

	const handleClickOnTestButton = () => {
		peekSensorConnection();
	};

	const handleDismissDialogPeek = () => {
		dispatch(peekSensorReset());
		setShowPeekModal(false);
	};
	const handleDismissDialogTblt = () => {
		dispatch(pollSensorTroubleshootingStop());
		setShowTbltModal(false);
	};

	useEffect(() => {
		dispatch(getSystemConnections());
	}, []);

	useEffect(() => {
		if (createSensorStatus === STATUS_ENUM.SUCCESS) {
			history.push(MAIN_ROUTES.SensorConfiguration);
		}
	}, [createSensorStatus]);

	useEffect(() => {
		if (peekState.response.data.length >= 1) setShowPeekModal(true);
	}, [peekState.response.data]);

	useEffect(() => {
		dispatch(restoreStatusStore());
		handleDismissDialogPeek();

		return () => {
			dispatch(resetCreateConfigurationStatus());
		};
	}, []);

	// TODO: Hook? alongside circular progress (as a comp)
	const pageLoading = useMemo<boolean>(
		() => !isSystemConnectionsInitialized,
		[isSystemConnectionsInitialized],
	);
	const buttonDisabledPeek =
		pageLoading || peekState.status === STATUS_ENUM.PENDING;
	const displayInlineSpinner = !pageLoading && buttonDisabledPeek;
	const spinnerStatusText = useMemo(() => {
		if (isCurrentlyTesting) return t('Validating Individual Fields');
		if (buttonDisabledPeek) return t('Testing Sensor');
		return '';
	}, [buttonDisabledPeek, isCurrentlyTesting]);
	// end

	return (
		<PageWrapper
			title={t('New Sensor')}
			back={{
				action: cancelConfig,
				label: t('Back to Sensor Configuration'),
			}}
			secondary={
				<div
					style={{
						display: 'flex',
						gap: '10px',
						flexDirection: 'row',
					}}>
					<SpinnerStatusIndicator
						visible={displayInlineSpinner}
						text={spinnerStatusText}
					/>
					{shouldRenderTest &&
						(shouldDisableTest ? (
							<Tooltip
								title={t('Not available for virtual sensors')}
								arrow>
								<span>
									<Button
										variant="outlined"
										color="secondary"
										disabled={shouldDisableTest}
										onClick={handleClickOnTestButton}
										data-test-id="test-conn-action-button">
										{t('Test')}
									</Button>
								</span>
							</Tooltip>
						) : (
							<Button
								variant="outlined"
								color="primary"
								disabled={displayInlineSpinner}
								onClick={handleClickOnTestButton}
								data-test-id="test-conn-action-button">
								{t('Test')}
							</Button>
						))}
					<Button
						variant="contained"
						color="primary"
						onClick={addNewSensorConfig}
						disabled={displayInlineSpinner}
						data-test-id="add-sensor--action-button">
						{t('Create Sensor')}
					</Button>
				</div>
			}
			isLoading={createSensorStatus === STATUS_ENUM.PENDING}
			applyPadding
			headerUnderline>
			<ContainerStyled maxWidth="lg">
				<BodyGridStyled>
					<FormCard
						systemConnections={rows}
						sensorTypes={sensorTypes}
						formData={formData}
						formErrors={formErrors}
						onMapChange={mapChange}
						onUpdateFormData={updateFormData}
						units={units}
						mode={SensorFormMode.ADD}
						sensorTroubleshooting={sensorTroubleshooting}
						isCurrentlyTesting={isCurrentlyTesting}
					/>
					<InfoCard title={t('Location')}>
						<MapCardStyle>
							<SensorConfigurationMap
								dataServices={dataSources || []}
								layerConfig={layerConfig}
								position={position || {}}
								mapMode={MapModes.Static}
							/>
						</MapCardStyle>
					</InfoCard>
				</BodyGridStyled>
			</ContainerStyled>
			<PeekDialog
				sensorId={formData.sensorId || t('Peek Data')}
				open={showPeekModal}
				onConfirm={handleDismissDialogPeek}
				onCancel={handleDismissDialogPeek}
				data={peekState.response.data}
			/>
			<SensorTroubleShootDialog
				open={showTbltModal}
				onConfirm={handleDismissDialogTblt}
				onCancel={handleDismissDialogTblt}
				sensorTbltingMsgs={sensorTbltingMsgs}
			/>
		</PageWrapper>
	);
};

export default NewSensor;
